import { useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { useForm, Controller } from 'react-hook-form'

import { useMutation } from '@hooks/graphql'
import SecureMarkdown from '@components/SecureMarkdown'
import TextEditor from '@components/TextEditor'
import { usePrompt } from '@hooks/prompt'
import { useFlashMessage } from '@components/FlashMessage'

import BlockLayout from './BlockLayout'
import BlockActions from './BlockActions'

const CREATE_REFLECTION_SUBMISSION_MUTATION = gql`
  mutation CreateReflectionSubmission($input: CreateReflectionSubmissionInput!) {
    createReflectionSubmission(input: $input) {
      errors {
        message
      }

      blockSubmission {
        content {
          ... on ReflectionSubmission {
            answer
          }
        }
      }
    }
  }
`

const ReflectionBlock = ({
  blockId,
  submissionId,
  assignmentId,
  question,
  moveToNextBlock,
  moveToPreviousBlock,
  isFirstBlock,
  isLastBlock,
  openSubmitModal,
  isSubmissionClosed,
  submissionStatus,
  answer,
  isTimeUp
}) => {
  const queryClient = useQueryClient()
  const { setFlashMessage } = useFlashMessage()

  const isDisabled = (submissionStatus === 'COMPLETED' || submissionStatus === 'RETURNED' || isSubmissionClosed)

  const { mutateAsync: createReflectionSubmission, isLoading } = useMutation({
    gqlMutation: CREATE_REFLECTION_SUBMISSION_MUTATION,
    onSuccess: () => { queryClient.invalidateQueries('educatorProjectSubmission') },
    onError: () => { setFlashMessage('Oops, we couldn\'t save your work. Please try again.', 'error') }
  })

  const { control, handleSubmit, formState: { errors, isDirty }, getValues } = useForm({
    mode: 'onTouched',
    defaultValues: { answer }
  })

  usePrompt(
    'You have unsaved changes. Are you sure you want to leave?',
    isDirty && !isTimeUp && !isLastBlock
  )

  const submitAnswer = async data => {
    if (!isDirty) return true

    try {
      await createReflectionSubmission({ input: { blockId, educatorProjectSubmissionId: submissionId, assignmentId, ...data } })
      return true
    } catch (error) {
      setFlashMessage('Oops, we couldn\'t save your work. Please try again.', 'error')
      return false
    }
  }

  const submit = async (data) => {
    if (isDisabled || isTimeUp) {
      if (isLastBlock) openSubmitModal()
      else moveToNextBlock()
      return
    }

    let shouldNavigate = true
    if (isDirty) {
      const success = await submitAnswer(data)
      shouldNavigate = success
    }

    if (shouldNavigate) {
      if (isLastBlock) {
        openSubmitModal()
      } else {
        moveToNextBlock()
      }
    }
  }

  const handleBack = async () => {
    if (isDirty) {
      const data = getValues()
      const success = await submitAnswer(data)
      if (success) {
        moveToPreviousBlock()
      }
    } else {
      moveToPreviousBlock()
    }
  }

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className='flex flex-col h-full'
    >
      <BlockLayout>
        <BlockLayout.Actions>
          <BlockActions
            isFirstBlock={isFirstBlock}
            isLastBlock={isLastBlock}
            onBack={handleBack}
            isSubmitting={isLoading}
            isSubmissionClosed={isSubmissionClosed}
            submissionStatus={submissionStatus}
            tooltipText="Submissions are closed for this pathway"
          />
        </BlockLayout.Actions>
        <BlockLayout.Content>
          <SecureMarkdown className='mb-3' content={question} />

          <Choose>
            <When condition={submissionStatus === 'COMPLETED' || submissionStatus === 'RETURNED'}>
              <div className='bg-gray-100 dark:bg-gray-800 p-3 rounded-lg mt-5'>
                <SecureMarkdown content={answer} />
              </div>
            </When>

            <Otherwise>
              <TextEditor.Container>
                <Controller
                  name='answer'
                  control={control}
                  render={({ field }) => (
                    <TextEditor
                      disabled={isDisabled || isTimeUp}
                      className='min-h-[200px]'
                      {...field}
                    />
                  )}
                />

                <TextEditor.Actions>
                  <TextEditor.ToolbarToggle />
                </TextEditor.Actions>
              </TextEditor.Container>

              <If condition={errors.answer}>
                <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.answer.message}</p>
              </If>
            </Otherwise>
          </Choose>
        </BlockLayout.Content>
      </BlockLayout>
    </form>
  )
}

export default ReflectionBlock
